import { useState } from 'react';
import { useController } from 'react-hook-form';
import { format, isValid, parseISO } from 'date-fns';
import { DatePickerBeta, IconButtonRounded, TextInput } from 'is24-corecss';
import { IconS24Calendar24 } from '@is24/cosma-ui-icons';
import { useIntl } from '@/i18n';

interface DatePickerProps {
  id: string;
  name: string;
  label: string;
  required?: boolean;
  maxDate: Date;
  maxDateErrorMessage?: string;
}

export const DatePicker = ({ id, name, label, required, maxDate, maxDateErrorMessage }: DatePickerProps) => {
  const { t } = useIntl();
  const calendarLabel = `${label}: ${t({ id: 'common.datePicker.calendar' })}`;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    defaultValue: '',
    rules: {
      required: required ? t({ id: 'common.errorMessages.inputRequired' }) : false,
      validate: (value: string) => {
        const date = parseISO(value);
        return (
          !value ||
          (isValid(date) && date <= maxDate) ||
          maxDateErrorMessage ||
          t({ id: 'common.errorMessages.invalidDate' })
        );
      },
    },
  });

  return (
    <DatePickerBeta.Root
      className='one-whole'
      aria-label={calendarLabel}
      open={calendarOpen}
      onOpenChange={setCalendarOpen}
      maxDate={maxDate}
      initialDisplayedMonth={maxDate}
      value={{ startDate: field.value ? parseISO(field.value) : undefined }}
      onValueChange={({ startDate }) => field.onChange(startDate ? format(startDate, 'yyyy-MM-dd') : '')}
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape' && calendarOpen) {
          event.stopPropagation();
          setCalendarOpen(false);
          event.currentTarget.querySelector<HTMLButtonElement>('button[aria-haspopup="dialog"]')?.focus();
        }
      }}
    >
      <DatePickerBeta.Trigger mode='manual' fixOnBlur={false}>
        {({ inputProps, buttonProps, isInvalid, validationError }) => {
          const errorMessage = isInvalid
            ? validationError === 'afterMaximumDate' && maxDateErrorMessage
              ? maxDateErrorMessage
              : t({ id: 'common.errorMessages.invalidDate' })
            : '';

          return (
            <TextInput.Root id={id} invalid={isInvalid || !!error} required={required}>
              <TextInput.Label>{label}</TextInput.Label>
              <TextInput.Input
                {...inputProps}
                ref={(input) => {
                  input?.setCustomValidity(errorMessage);
                  field.ref(input);
                }}
                onBlur={(event) => {
                  inputProps.onBlur?.(event);
                  field.onBlur();
                }}
                onKeyDown={(event) => {
                  inputProps.onKeyDown?.(event);
                  if (event.key === 'Enter' && event.currentTarget.form) {
                    event.preventDefault();
                    // Commit the manually entered date before submitting the form.
                    event.currentTarget.blur();
                    event.currentTarget.form.requestSubmit();
                  }
                }}
                name={name}
                inputMode='numeric'
                autoComplete='off'
                placeholder='TT.MM.JJJJ'
                pattern='[0-9]{2}[.][0-9]{2}[.][0-9]{4}'
                endElement={
                  <IconButtonRounded {...buttonProps} type='button' variant='textDefault' aria-label={calendarLabel}>
                    <IconS24Calendar24 />
                  </IconButtonRounded>
                }
              />
              <TextInput.ErrorText role='alert'>{errorMessage || error?.message}</TextInput.ErrorText>
            </TextInput.Root>
          );
        }}
      </DatePickerBeta.Trigger>
      {calendarOpen && (
        <DatePickerBeta.Content>
          <DatePickerBeta.Header>
            <DatePickerBeta.Header.Title>{label}</DatePickerBeta.Header.Title>
            <DatePickerBeta.Header.CloseButton />
          </DatePickerBeta.Header>
          <DatePickerBeta.Calendar />
        </DatePickerBeta.Content>
      )}
    </DatePickerBeta.Root>
  );
};
